import { useCallback, useMemo } from 'react';

import { NotificationActions } from 'domains/notifications/store/NotificationActions';
import { referralProgramTranslation } from 'modules/referralProgram/translation';
import { removeReferralCodeFromUrl } from 'modules/referralProgram/utils/removeReferralCodeFromUrl';
import { useAppDispatch } from 'store/useAppDispatch';
import { useDialog } from 'modules/common/hooks/useDialog';
import { useSavedReferralCode } from 'modules/referralProgram/hooks/useSavedReferralCode';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import { ISwitchAccountDialogProps } from '../SwitchAccountDialog';
import { useSelectPersonalAccount } from './useSelectPersonalAccount';

const { showNotification } = NotificationActions;

export interface IUseSwitchAccountDialogProps {
  cover: string;
  referralProgramName: string | undefined;
}

export const useSwitchAccountDialog = ({
  cover,
  referralProgramName,
}: IUseSwitchAccountDialogProps) => {
  const {
    isOpened,
    onClose: handleClose,
    onOpen: handleSwitchAccountDialogOpen,
  } = useDialog();

  const { handleRemoveSavedReferralCode } = useSavedReferralCode();

  const {
    keys: { branded, unbranded },
    t,
  } = useTranslation(referralProgramTranslation);
  const isBranded = Boolean(referralProgramName);
  const keys = isBranded ? branded : unbranded;

  const dispatch = useAppDispatch();

  const onClose = useCallback(() => {
    removeReferralCodeFromUrl();
    handleRemoveSavedReferralCode();

    handleClose();

    dispatch(
      showNotification({
        message: t(keys.activationRejectedErrorMessage),
        severity: 'error',
        title: t(keys.activationRejectedErrorTitle, { referralProgramName }),
      }),
    );
  }, [
    dispatch,
    handleClose,
    handleRemoveSavedReferralCode,
    keys,
    referralProgramName,
    t,
  ]);

  const { handleSelectPersonalAccount, isPersonalAccountSelecting } =
    useSelectPersonalAccount();

  const onSwitchButtonClick = useCallback(() => {
    handleSelectPersonalAccount();

    handleClose();
  }, [handleClose, handleSelectPersonalAccount]);

  const switchAccountDialogProps = useMemo(
    (): ISwitchAccountDialogProps => ({
      cover,
      isBranded,
      isSwitching: isPersonalAccountSelecting,
      onClose,
      onSwitchButtonClick,
      open: isOpened,
    }),
    [
      cover,
      isBranded,
      isOpened,
      isPersonalAccountSelecting,
      onClose,
      onSwitchButtonClick,
    ],
  );

  return { handleSwitchAccountDialogOpen, switchAccountDialogProps };
};
